/**
 * ForcePasswordChange · v8.5.63 · piel — bloquea el portal si
 * user.debe_cambiar_password. Sigue siendo overlay modal (tiene que
 * bloquear sobre cualquier pantalla ya autenticada, no es una ruta
 * navegable), pero con la tipografía/tokens de la piel: título
 * font-display 30px, línea 15px explicando, error como línea --danger
 * (no caja), sin emoji.
 */
import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ForcePasswordChange() {
  const { user, logout } = useAuth();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  if (!user?.debe_cambiar_password) return null;

  async function enviar(e) {
    e?.preventDefault();
    setError("");
    if (nueva.length < 8) { setError("Mínimo 8 caracteres"); return; }
    if (nueva !== confirm) { setError("Las contraseñas no coinciden"); return; }
    if (nueva === actual) { setError("La nueva contraseña debe ser distinta"); return; }
    setEnviando(true);
    try {
      await api.changePassword({ password_actual: actual, password_nueva: nueva });
      // Recargar para que el AuthContext refresque user con debe_cambiar_password=false
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviando(false);
    }
  }

  // v8.5.68 · fix auditoría móvil · 16px (evita el auto-zoom de iOS al enfocar).
  const inputCls = "w-full px-3 py-2.5 bg-[var(--surface)] border border-[var(--line-control)] rounded-[14px] text-[16px] focus:outline-none focus:border-[var(--accent)] text-[var(--ink)]";

  return (
    // v8.5.68 · fix auditoría móvil · items-start+overflow-y-auto en vez de
    // solo items-center: este modal es un gate obligatorio (sin forma de
    // cerrarlo salvo "Cerrar sesión") — con el teclado abierto en un
    // iPhone chico, "items-center" sin scroll dejaba el botón "Guardar y
    // entrar" fuera de la pantalla e inalcanzable.
    <div className="fixed inset-0 z-[var(--z-notice)] bg-[var(--ink)]/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={enviar}
            className="shadow-overlay bg-[var(--surface)] rounded-[20px] max-w-md w-full p-6 space-y-4 my-8 sm:my-0">
        <div>
          <h2 className="font-display text-[24px] leading-tight text-[var(--ink)]">Primera vez acá</h2>
          <p className="text-[15px] text-[var(--muted)] mt-1.5">
            Para tu seguridad, antes de empezar tenés que cambiar la contraseña que te dio The Crew por una propia.
          </p>
        </div>

        <div>
          <label className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-1.5 block">
            Contraseña actual (la que te enviaron)
          </label>
          <input type={showPwd ? "text" : "password"} value={actual} onChange={e => setActual(e.target.value)}
                 required autoFocus autoComplete="current-password" className={inputCls}/>
        </div>

        <div>
          <label className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-1.5 block">
            Nueva contraseña
          </label>
          <input type={showPwd ? "text" : "password"} value={nueva} onChange={e => setNueva(e.target.value)}
                 required minLength={8} autoComplete="new-password" placeholder="Mínimo 8 caracteres" className={inputCls}/>
        </div>

        <div>
          <label className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-1.5 block">
            Confirmar nueva contraseña
          </label>
          <input type={showPwd ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)}
                 required autoComplete="new-password" className={inputCls}/>
        </div>

        <label className="flex items-center gap-1.5 text-[13px] text-[var(--muted)] cursor-pointer select-none">
          <input type="checkbox" checked={showPwd} onChange={e => setShowPwd(e.target.checked)}/>
          Mostrar contraseñas
        </label>

        {error && <p className="text-[13px] text-[var(--danger)]">{error}</p>}

        <div className="flex justify-between items-center pt-2">
          <button type="button" onClick={logout}
                  className="text-[13px] text-[var(--muted)] hover:text-[var(--ink-2)] hover:underline">
            Cerrar sesión
          </button>
          <button type="submit" disabled={enviando}
                  className="px-5 py-2 bg-[var(--ink)] text-[var(--surface)] rounded-full text-sm font-medium disabled:opacity-50 hover:opacity-90">
            {enviando ? "Guardando…" : "Guardar y entrar"}
          </button>
        </div>
      </form>
    </div>
  );
}
